import { useState } from 'react';
import {
  type Department,
  type ReagentItemStock,
  type RequisitionDetail,
  type Supplier,
  createRequisitionSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useMutation } from '#client/hooks/useMutation';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import {
  EMPTY_REQUISITION_LINE,
  describeSuggestions,
  initialRequisitionFields,
  initialRequisitionLines,
  linesFromSuggestions,
} from './requisition.logic';
import { useRepeater } from '#client/hooks/useRepeater';
import { RequisitionHeaderCard, RequisitionLinesCard } from './RequisitionFormSections';
import { nameOptions } from '#client/lib/form-choices';
import { suggestRequisitionLines } from './procurement.service';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.requisitions;

export function RequisitionFormPage() {
  return (
    <ResourceFormShell<RequisitionDetail> path={PATH} noun="reagent order">
      {({ id, record }) => <RequisitionForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function RequisitionForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: RequisitionDetail | undefined;
}) {
  const reagents = useAllRecords<ReagentItemStock>({
    path: RESOURCE.reagentItems,
    extraQuery: { active: 'true' },
  });
  const suppliers = useAllRecords<Supplier>({ path: RESOURCE.suppliers });
  const departments = useAllRecords<Department>({ path: RESOURCE.departments });

  const lines = useRepeater(initialRequisitionLines(record), EMPTY_REQUISITION_LINE);
  const suggestRequest = useMutation('The reorder list could not be loaded.');
  const [suggestionMessage, setSuggestionMessage] = useState<string | null>(null);

  const { values, set } = useFormValues(initialRequisitionFields(record));

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createRequisitionSchema,
    noun: 'Reagent order',
  });

  /**
   * Fill the order from everything at or below its reorder level.
   *
   * The endpoint returns a *preview*, not a saved draft — so this replaces what
   * is on screen and the operator still has to save. An endpoint that created a
   * requisition every time somebody glanced at the reorder list would fill the
   * inbox with drafts nobody asked for.
   */
  async function handleFillFromLowStock() {
    const outcome = await suggestRequest.run(() =>
      suggestRequisitionLines({ departmentId: values.department_id ?? null }),
    );
    if (!outcome.ok) return;

    lines.replace(linesFromSuggestions(outcome.value));
    setSuggestionMessage(describeSuggestions(outcome.value.length));
  }

  return (
    <form onSubmit={form.handleSubmit({ ...values, lines: lines.rows })} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}
        {suggestRequest.errorMessage && (
          <Banner tone="critical">{suggestRequest.errorMessage}</Banner>
        )}
        {suggestionMessage && <Banner tone="info">{suggestionMessage}</Banner>}

        <RequisitionHeaderCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          supplierOptions={nameOptions(suppliers.items)}
          departmentOptions={nameOptions(departments.items)}
        />

        <RequisitionLinesCard
          lines={lines}
          reagents={reagents.items}
          linesError={form.errors.lines}
          isSuggesting={suggestRequest.isPending}
          onFillFromLowStock={() => void handleFillFromLowStock()}
        />

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Create the order'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
